import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  ActionIcon,
  Loader,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { IconCopy, IconPlus, IconTrash } from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import {
  buildEmptyRateObject,
  computeTotalAndGrossPercent,
  decimalToPercent,
  getYearColumnDefs,
  getYearKeys,
  percentToDecimal,
} from "../helpers/commissionScheduleYears";
import { DATA_CURRENCY, DATA_PAY_MODES } from "../data/options";
import {
  blankCondition,
  conditionFieldOptions,
  getConditionField,
  operatorsForField,
  parseConditionList,
  serializeConditionList,
} from "../helpers/commissionConditionFields";

const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const toDateValue = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const asDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const toDateInput = (value) => {
  const date = asDate(value);
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const ratesFromDecimal = (rate) => {
  const result = buildEmptyRateObject(0);
  for (const key of getYearKeys()) {
    result[key] = decimalToPercent(rate?.[key]);
  }
  return result;
};

const ratesToDecimal = (rate) => {
  const result = {};
  for (const key of getYearKeys()) {
    result[key] = percentToDecimal(rate?.[key]);
  }
  return result;
};

const blankRow = () => ({
  conditions: [],
  basicRate: buildEmptyRateObject(0),
  overrideRate: buildEmptyRateObject(0),
  extraRate: buildEmptyRateObject(0),
});

const blankForm = () => ({
  effectiveDate: today(),
  endDate: null,
  professionalInvestor: "false",
  rows: [blankRow()],
});

const formFromScheduleProduct = (scheduleProduct, mode) => {
  const rows = (scheduleProduct?.commissionTable || []).map((row) => ({
    conditions: parseConditionList(row.conditions),
    basicRate: ratesFromDecimal(row.basicRate),
    overrideRate: ratesFromDecimal(row.overrideRate),
    extraRate: ratesFromDecimal(row.extraRate),
  }));

  return {
    effectiveDate: mode === "create" ? today() : toDateValue(scheduleProduct?.effectiveDate),
    endDate: mode === "create" ? null : toDateValue(scheduleProduct?.endDate),
    professionalInvestor: scheduleProduct?.professionalInvestor ? "true" : "false",
    rows: rows.length ? rows : [blankRow()],
  };
};

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const sectionDivider = {
  borderLeft: "3px solid #343a40",
};

const dropdownProps = {
  withinPortal: true,
  zIndex: 1000,
  dropdownPosition: "flip",
  maxDropdownHeight: 480,
};

const RATE_SECTIONS = [
  { key: "grossRate", label: "Gross Rate(%)", editable: false, color: "#FFC000" },
  { key: "totalRate", label: "Total Rate(%)", editable: false, color: "#FFC000" },
  { key: "basicRate", label: "Basic Rate(%)", editable: true, color: "#92D050" },
  { key: "overrideRate", label: "Override Rate(%)", editable: true, color: "#92D050" },
  { key: "extraRate", label: "Extra Rate(%)", editable: true, color: "#92D050" },
];

const ProductCommissionScheduleEditor = ({
  mode = "create",
  productId,
  scheduleProductId = null,
  versionName = "",
  onClose,
  onSaved,
}) => {
  const [api] = useServerApi();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(blankForm);
  const yearColumns = getYearColumnDefs();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!scheduleProductId) {
        setForm(blankForm());
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const scheduleProduct = await api.getById({
          apiEntity: "commissionScheduleProduct",
          id: scheduleProductId,
        });
        if (cancelled) return;
        if (!scheduleProduct) throw new Error("Commission version not found");
        setForm(formFromScheduleProduct(scheduleProduct, mode));
      } catch (error) {
        if (!cancelled) {
          showNotification({
            color: "red",
            title: "Unable to load commission",
            message: error?.message || "Please try again",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [scheduleProductId, mode]);

  const updateRow = (rowIndex, patch) => {
    setForm((current) => ({
      ...current,
      rows: current.rows.map((row, index) =>
        index === rowIndex ? { ...row, ...patch } : row
      ),
    }));
  };

  const updateConditions = (rowIndex, conditions) => {
    updateRow(rowIndex, { conditions });
  };

  const updateCondition = (rowIndex, conditionIndex, patch) => {
    const conditions = form.rows[rowIndex].conditions.map((condition, index) => {
      if (index !== conditionIndex) return condition;
      const next = { ...condition, ...patch };
      if (patch.field && patch.field !== condition.field) {
        const field = getConditionField(patch.field);
        const operators = operatorsForField(patch.field);
        next.operator = operators[0]?.value || "";
        next.value = "";
        next.customField = "";
        next.unrecognized = false;
        next.raw = "";
        if (field?.valueType === "number" && !operators.some((item) => item.value === "≥")) {
          next.operator = operators[0]?.value || "=";
        }
      }
      return next;
    });
    updateConditions(rowIndex, conditions);
  };

  const updateRate = (rowIndex, rateKey, yearKey, value) => {
    const number = Number(value);
    setForm((current) => ({
      ...current,
      rows: current.rows.map((row, index) => {
        if (index !== rowIndex) return row;
        return {
          ...row,
          [rateKey]: {
            ...row[rateKey],
            [yearKey]: Number.isFinite(number) ? number : 0,
          },
        };
      }),
    }));
  };

  const addRow = () => {
    setForm((current) => ({ ...current, rows: [...current.rows, blankRow()] }));
  };

  const copyRow = (rowIndex) => {
    setForm((current) => {
      const source = current.rows[rowIndex];
      if (!source) return current;
      const copy = {
        conditions: source.conditions.map((condition) => ({ ...condition })),
        basicRate: { ...source.basicRate },
        overrideRate: { ...source.overrideRate },
        extraRate: { ...source.extraRate },
      };
      const rows = [...current.rows];
      rows.splice(rowIndex + 1, 0, copy);
      return { ...current, rows };
    });
  };

  const removeRow = (rowIndex) => {
    setForm((current) => ({
      ...current,
      rows: current.rows.filter((_, index) => index !== rowIndex),
    }));
  };

  const save = async () => {
    if (!productId) return;
    const effectiveDate = asDate(form.effectiveDate);
    const endDate = asDate(form.endDate);
    if (!effectiveDate) {
      showNotification({
        color: "red",
        title: "Start date is required",
        message: "Choose the date this commission version starts.",
      });
      return;
    }
    if (form.endDate && !endDate) {
      showNotification({
        color: "red",
        title: "End date is invalid",
        message: "Use a date in YYYY-MM-DD format.",
      });
      return;
    }
    if (endDate && endDate < effectiveDate) {
      showNotification({
        color: "red",
        title: "End date is before the start date",
        message: "The end date must be on or after the start date.",
      });
      return;
    }

    const body = {
      effectiveDate: toDateInput(effectiveDate),
      endDate: toDateInput(endDate),
      professionalInvestor: form.professionalInvestor === "true",
      commissionTable: form.rows.map((row) => ({
        conditions: serializeConditionList(row.conditions),
        basicRate: ratesToDecimal(row.basicRate),
        overrideRate: ratesToDecimal(row.overrideRate),
        extraRate: ratesToDecimal(row.extraRate),
      })),
    };

    setSaving(true);
    try {
      if (mode === "edit") {
        await api.Product.updateCommissionScheduleProduct(productId, {
          ...body,
          scheduleProductId,
        });
      } else {
        await api.Product.createCommissionScheduleProduct(productId, {
          ...body,
          sourceScheduleProductId: scheduleProductId || undefined,
        });
      }
      showNotification({
        color: "teal",
        title: mode === "edit" ? "Commission updated" : "Commission version added",
        message:
          mode === "edit"
            ? "This product commission version has been saved."
            : "The new commission version is saved for this product.",
      });
      onSaved?.(mode);
    } catch (error) {
      showNotification({
        color: "red",
        title: "Unable to save commission",
        message: error?.message || "Please try again",
      });
    } finally {
      setSaving(false);
    }
  };

  const displayRate = (row, tabValue, yearKey) => {
    if (tabValue === "totalRate" || tabValue === "grossRate") {
      const preview = computeTotalAndGrossPercent(
        row.basicRate[yearKey],
        row.overrideRate[yearKey],
        row.extraRate[yearKey]
      );
      return formatPercent(tabValue === "totalRate" ? preview.total : preview.gross);
    }
    return null;
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={mode === "edit" ? "Edit product commission" : "Add product commission"}
      fullScreen
      closeOnClickOutside={false}
    >
    <Stack spacing="md">
      <Group position="apart" align="flex-start">
        <div>
          <Text weight={600}>
            {mode === "edit" ? "Edit commission" : "Add commission"}
            {versionName ? ` ${versionName}` : ""}
          </Text>
          <Text size="xs" color="dimmed" mt={4}>
            Enter Basic, Override, and Extra as percentages. Gross and Total are calculated.
          </Text>
        </div>
        <Group spacing="xs">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving}>
            Save
          </Button>
        </Group>
      </Group>

      {loading ? (
        <Group position="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <>
          <Alert color={mode === "edit" ? "orange" : "blue"} variant="light">
            {mode === "edit"
              ? "Saving updates this commission version. Orders that already saved these rates keep them until commission is regenerated."
              : "This adds a commission version for this product only. The imported schedule file stays unchanged. If the previous version for the same PI type has no end date, it ends the day before this start date."}
          </Alert>

          <Group align="flex-end" spacing="md">
            <DatePicker
              label="Start"
              placeholder="Start date"
              value={form.effectiveDate}
              onChange={(value) =>
                setForm((current) => ({ ...current, effectiveDate: value }))
              }
              inputFormat="YYYY-MM-DD"
              allowFreeInput
              clearable={false}
              required
              style={{ width: 180 }}
            />
            <DatePicker
              label="End"
              placeholder="No end date"
              value={form.endDate}
              onChange={(value) =>
                setForm((current) => ({ ...current, endDate: value }))
              }
              inputFormat="YYYY-MM-DD"
              allowFreeInput
              clearable
              style={{ width: 180 }}
            />
            <Select
              label="Investor type"
              data={[
                { value: "false", label: "Non-PI" },
                { value: "true", label: "PI" },
              ]}
              value={form.professionalInvestor}
              onChange={(value) =>
                setForm((current) => ({
                  ...current,
                  professionalInvestor: value || "false",
                }))
              }
              style={{ width: 160 }}
            />
          </Group>

          <div style={{ overflowX: "auto", width: "100%" }}>
            <Table
              fontSize="xs"
              verticalSpacing="xs"
              sx={{
                width: "max-content",
                minWidth: "100%",
                "th, td": { whiteSpace: "nowrap", verticalAlign: "middle" },
              }}
            >
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    style={{
                      minWidth: 220,
                      background: "#C6EFCE",
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                    }}
                  >
                    Conditions
                  </th>
                  {RATE_SECTIONS.map((section) => (
                    <th
                      key={section.key}
                      colSpan={yearColumns.length}
                      style={{
                        background: section.color,
                        textAlign: "center",
                        ...sectionDivider,
                      }}
                    >
                      {section.label}
                    </th>
                  ))}
                  <th rowSpan={2} style={{ background: "#C6EFCE" }} />
                </tr>
                <tr>
                  {RATE_SECTIONS.map((section) =>
                    yearColumns.map((column, columnIndex) => (
                      <th
                        key={`${section.key}-${column.key}`}
                        style={{
                          background: "#C6EFCE",
                          textAlign: "center",
                          ...(columnIndex === 0 ? sectionDivider : null),
                        }}
                      >
                        {column.label}
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {form.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td
                      style={{
                        minWidth: 460,
                        background: "white",
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                      }}
                    >
                      <Stack spacing={6}>
                        {row.conditions.map((condition, conditionIndex) => {
                          const field = getConditionField(condition.field);
                          const operators = operatorsForField(condition.field);
                          return (
                            <Group
                              key={conditionIndex}
                              spacing={4}
                              noWrap
                              align="flex-end"
                            >
                              {condition.unrecognized ? (
                                <TextInput
                                  aria-label={`Unrecognized condition ${rowIndex + 1} ${conditionIndex + 1}`}
                                  value={condition.raw}
                                  readOnly
                                  size="xs"
                                  styles={{ input: { width: 280 } }}
                                />
                              ) : (
                                <>
                                  <Select
                                    aria-label={`Condition field ${rowIndex + 1} ${conditionIndex + 1}`}
                                    data={conditionFieldOptions}
                                    value={condition.field}
                                    onChange={(value) =>
                                      updateCondition(rowIndex, conditionIndex, {
                                        field: value || "Insured Age",
                                      })
                                    }
                                    size="xs"
                                    searchable
                                    {...dropdownProps}
                                    styles={{ input: { width: 180 } }}
                                  />
                                  {operators.length > 0 && (
                                    <Select
                                      aria-label={`Condition operator ${rowIndex + 1} ${conditionIndex + 1}`}
                                      data={operators}
                                      value={condition.operator}
                                      onChange={(value) =>
                                        updateCondition(rowIndex, conditionIndex, {
                                          operator: value || "=",
                                        })
                                      }
                                      size="xs"
                                      {...dropdownProps}
                                      styles={{ input: { width: 70 } }}
                                    />
                                  )}
                                  {field?.valueType === "number" && (
                                    <NumberInput
                                      aria-label={`Condition value ${rowIndex + 1} ${conditionIndex + 1}`}
                                      value={condition.value === "" ? undefined : Number(condition.value)}
                                      onChange={(value) =>
                                        updateCondition(rowIndex, conditionIndex, {
                                          value: value == null || value === "" ? "" : String(value),
                                        })
                                      }
                                      min={0}
                                      hideControls
                                      size="xs"
                                      styles={{ input: { width: 90 } }}
                                    />
                                  )}
                                  {field?.valueType === "currency" && (
                                    <Select
                                      aria-label={`Condition value ${rowIndex + 1} ${conditionIndex + 1}`}
                                      data={DATA_CURRENCY}
                                      value={condition.value || null}
                                      onChange={(value) =>
                                        updateCondition(rowIndex, conditionIndex, {
                                          value: value || "",
                                        })
                                      }
                                      size="xs"
                                      searchable
                                      {...dropdownProps}
                                      styles={{ input: { width: 100 } }}
                                    />
                                  )}
                                  {field?.valueType === "payMode" && (
                                    <Select
                                      aria-label={`Condition value ${rowIndex + 1} ${conditionIndex + 1}`}
                                      data={DATA_PAY_MODES}
                                      value={condition.value || null}
                                      onChange={(value) =>
                                        updateCondition(rowIndex, conditionIndex, {
                                          value: value || "",
                                        })
                                      }
                                      size="xs"
                                      {...dropdownProps}
                                      styles={{ input: { width: 140 } }}
                                    />
                                  )}
                                  {field?.valueType === "custom" && (
                                    <>
                                      <TextInput
                                        aria-label={`Custom field ${rowIndex + 1} ${conditionIndex + 1}`}
                                        placeholder="Field"
                                        value={condition.customField}
                                        onChange={(event) =>
                                          updateCondition(rowIndex, conditionIndex, {
                                            customField: event.currentTarget.value,
                                          })
                                        }
                                        size="xs"
                                        styles={{ input: { width: 90 } }}
                                      />
                                      <TextInput
                                        aria-label={`Custom value ${rowIndex + 1} ${conditionIndex + 1}`}
                                        placeholder="Value"
                                        value={condition.value}
                                        onChange={(event) =>
                                          updateCondition(rowIndex, conditionIndex, {
                                            value: event.currentTarget.value,
                                          })
                                        }
                                        size="xs"
                                        styles={{ input: { width: 90 } }}
                                      />
                                    </>
                                  )}
                                </>
                              )}
                              <ActionIcon
                                color="red"
                                variant="subtle"
                                mb={2}
                                onClick={() =>
                                  updateConditions(
                                    rowIndex,
                                    row.conditions.filter((_, index) => index !== conditionIndex)
                                  )
                                }
                                aria-label={`Remove condition ${rowIndex + 1} ${conditionIndex + 1}`}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Group>
                          );
                        })}
                        <Group spacing={4}>
                          <Button
                            variant="subtle"
                            size="xs"
                            compact
                            leftIcon={<IconPlus size={12} />}
                            onClick={() =>
                              updateConditions(rowIndex, [...row.conditions, blankCondition()])
                            }
                          >
                            Add condition
                          </Button>
                          <Button
                            variant="subtle"
                            size="xs"
                            compact
                            leftIcon={<IconCopy size={12} />}
                            onClick={() => copyRow(rowIndex)}
                          >
                            Copy condition row
                          </Button>
                        </Group>
                      </Stack>
                    </td>
                    {RATE_SECTIONS.map((section) =>
                      yearColumns.map((column, columnIndex) => (
                        <td
                          key={`${section.key}-${column.key}`}
                          style={columnIndex === 0 ? sectionDivider : undefined}
                        >
                          {section.editable ? (
                            <NumberInput
                              aria-label={`${section.label} ${column.label} row ${rowIndex + 1}`}
                              value={row[section.key][column.key]}
                              onChange={(value) =>
                                updateRate(rowIndex, section.key, column.key, value)
                              }
                              precision={2}
                              min={0}
                              max={500}
                              step={0.01}
                              hideControls
                              size="xs"
                              styles={{ input: { width: 72, textAlign: "right" } }}
                            />
                          ) : (
                            <Text size="xs" align="right">
                              {displayRate(row, section.key, column.key)}
                            </Text>
                          )}
                        </td>
                      ))
                    )}
                    <td>
                      {form.rows.length > 1 && (
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => removeRow(rowIndex)}
                          aria-label={`Remove condition row ${rowIndex + 1}`}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <Group position="apart">
            <Button variant="default" leftIcon={<IconPlus size={14} />} onClick={addRow}>
              Add condition row
            </Button>
            <Group spacing="xs">
              <Button variant="default" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={save} loading={saving}>
                Save
              </Button>
            </Group>
          </Group>
        </>
      )}
    </Stack>
    </Modal>
  );
};

export default ProductCommissionScheduleEditor;
